import type { TradeFormValues } from '../useCreateTradeForm';

/**
 * LR-495: create-form initial values. MUST stay a factory, not a module-level
 * const — trade_date / trade_time / settlement_date / maturity_date are computed
 * from "now" on each call so the form opens with today's dates. A frozen const
 * would pin them to bundle-load time (the first page load), a real regression.
 */
export function makeCreateTradeFormDefaults(): TradeFormValues {
  // Trade date and time are both UTC (LR-809) and cut from one instant: a second
  // clock read could straddle midnight and pair one day's date with the next's time.
  const now = new Date();
  const nowIso = now.toISOString();
  const tomorrow = new Date(now.getTime() + 86400000).toISOString().split('T')[0];
  return {
    symbol_id: '',
    description: '',
    isin: '',
    currency: 'USD',
    booking_center: '',
    custodian_account: '',
    counterparty_name: '',
    counterparty_account: '',
    trade_type: 'lend',
    collateral_type: 'cash',
    // LR-586: no pool is pre-picked - the dropdown filters by counterparty,
    // product and currency, none of which are known when the form opens.
    cash_pool: null,
    quantity: 0,
    // LR-621 (QA r3): '' not '0' — the pristine disabled field must not
    // display a bookable-looking zero. Every selection path overwrites the
    // field via snapshotGrossPrice (an absent snapshot price stays '').
    gross_price: '',
    current_market_price: '',
    // 10000 bps = 100% margin (no overcollateralization). Default chosen
    // so the form opens to a valid, business-sensible value instead of
    // empty/0 (backend accepts margin_bps in [100, 19900]).
    margin_bps: 10000,
    rate_entry_mode: 'rebate',
    rate_type: 'floating',
    benchmark_rate: '0.0000',
    benchmark_name: undefined,
    spread_bps: undefined,
    fee_percent: undefined,
    day_count_convention: null,
    dividend_passthrough_percent: '100',
    trade_reason: '',
    trade_reason_comment: '',
    trade_date: nowIso.slice(0, 10),
    trade_time: nowIso.slice(11, 19),
    settlement_date: tomorrow,
    maturity_date: tomorrow,
    term_type: 'open',
    notice_period_days: 1,
    delivery_type: 'dvp',
    settlement_venue: 'dtc',
    cash_settlement_place: undefined as number | undefined,
    securities_settlement_place: undefined as number | undefined,
    settlement_currency: 'USD',
    fx_rate: undefined,
    net_settlement_converted_input: undefined,
    counterparty_id: '',
    custodian_id: '',
    agreement_id: '',
    comments: '',
    trade_duration: 'OPEN',
    collateral_isin: '',
    collateral_symbol_id: '',
    collateral_quantity: '',
    collateral_cob_price: '',
    collateral_current_market_price: '',
    collateral_percent: '100.00',
    pre_delivery_party: '',
    collateral_settlement_place: null,
  };
}
